import {useQuery} from '@tanstack/react-query'
import type {SuggestLocation} from '@yandex-delivery/api/model/suggest'
import {useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {type AddressPin, useFetchAddressPin} from '../../../../../api/address'
import {useConfigContext} from '../../../../../contexts/ConfigContext'

/**
 * Предзаполняет адрес отправления текущей локацией пользователя.
 * @param fieldName Название поля в стейте
 * @param isSource Является ли адрес - точкой отправления ("Откуда")
 */
export const usePrefetchAddress = (fieldName: string, isSource: boolean) => {
  const {location: configLocation} = useConfigContext()
  const {getValues} = useFormContext()
  const {field} = useController({name: fieldName})

  const getGeolocationFromBrowser = (): Promise<SuggestLocation> =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        ({coords}) => {
          resolve([coords.longitude, coords.latitude])
        },
        reject,
        {
          enableHighAccuracy: true,
          maximumAge: 60000,
          timeout: 2000,
        },
      )
    })

  const updateState = useCallback(
    (addressPin: AddressPin) => {
      field.onChange(addressPin)
    },
    [field],
  )

  const {mutate: mutateAddressPin} = useFetchAddressPin({onSuccess: updateState})

  const {isLoading} = useQuery({
    queryKey: ['get-user-location'],
    queryFn: async () => {
      const value = getValues(fieldName)
      if (!isSource || value?.title) return

      try {
        const location = await getGeolocationFromBrowser()
        mutateAddressPin({coordinates: location})
      } catch {
        mutateAddressPin({coordinates: configLocation})
      }
    },
  })

  return {isLoading} as const
}
